// src/pages/LeaderboardsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { firestore, collection, getDocs, doc, getDoc } from '../firebase-config';
import './LeaderboardsPage.css';

const ITEMS_PER_PAGE = 50;

const ALL_COLORS = [
  'WHITE', 'BLACK', 'BLUE', 'PINK', 'BROWN', 'CYAN', 
  'GREEN', 'GRAY', 'LIGHT_BLUE', 'LIGHT_GRAY', 'LIME', 
  'MAGENTA', 'ORANGE', 'RED', 'PURPLE', 'YELLOW'
];

const LeaderboardsPage = () => {
  const navigate = useNavigate();
  const { type, metric } = useParams();
  
  const getInitialTab = () => {
    if (type === 'player' || type === 'players') return 'players';
    if (type === 'color' || type === 'colour' || type === 'colors' || type === 'colours') return 'colors';
    return 'players';
  };

  const getInitialPlayerMetric = () => {
    if (metric === 'total-painted' || metric === 'total') return 'total';
    if (metric === 'block-coverage' || metric === 'owned') return 'owned';
    return 'total';
  };

  const getInitialColorMetric = () => {
    if (metric === 'currently-on-map' || metric === 'current') return 'current';
    if (metric === 'total-ever-painted' || metric === 'ever' || metric === 'all-time') return 'ever';
    return 'current';
  };

  const [selectedColor, setSelectedColor] = useState('all');
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [playerMetric, setPlayerMetric] = useState(getInitialPlayerMetric());
  const [colorMetric, setColorMetric] = useState(getInitialColorMetric());
  const [playerLeaderboard, setPlayerLeaderboard] = useState([]);
  const [colorLeaderboard, setColorLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (activeTab === 'players') {
      navigate(`/leaderboards/player/${playerMetric === 'total' ? 'total-painted' : 'block-coverage'}`, { replace: true });
    } else if (activeTab === 'colors') {
      navigate(`/leaderboards/colour/${colorMetric === 'current' ? 'currently-on-map' : 'total-ever-painted'}`, { replace: true });
    }
  }, [activeTab, playerMetric, colorMetric, navigate]);

// Update the useEffect to reload when color changes
useEffect(() => {
  setCurrentPage(1);
  loadLeaderboards();
}, [playerMetric, colorMetric, selectedColor]); // Added selectedColor

  const loadLeaderboards = async () => {
    setLoading(true);
    await Promise.all([
      loadPlayerLeaderboard(),
      loadColorLeaderboard()
    ]);
    setLoading(false);
  };

  const loadPlayerLeaderboard = async () => {
  try {
    const playersSnapshot = await getDocs(collection(firestore, 'players'));
    const players = [];

    for (const playerDoc of playersSnapshot.docs) {
      const data = playerDoc.data();
      
      let metric = 0;
      
      if (selectedColor === 'all') {
        // Regular behavior - total or owned
        if (playerMetric === 'total') {
          metric = data.blocksPainted || 0;
        } else if (playerMetric === 'owned') {
          const ownedBlocksSnapshot = await getDocs(
            collection(firestore, 'players', playerDoc.id, 'ownedBlocks')
          );
          metric = ownedBlocksSnapshot.size;
        }
      } else {
        // Color-specific filtering
        if (playerMetric === 'total') {
          // Get color stats from player's colourStats subcollection
          const colorStatsDoc = await getDoc(
            doc(firestore, 'players', playerDoc.id, 'colourStats', 'stats')
          );
          
          if (colorStatsDoc.exists()) {
            metric = colorStatsDoc.data()[selectedColor] || 0;
          }
        } else if (playerMetric === 'owned') {
          // Count owned blocks of specific color
          const ownedBlocksSnapshot = await getDocs(
            collection(firestore, 'players', playerDoc.id, 'ownedBlocks')
          );
          
          metric = 0;
          ownedBlocksSnapshot.forEach(doc => {
            const blockData = doc.data();
            if (blockData.colour === selectedColor) {
              metric++;
            }
          });
        }
      }

      players.push({
        uuid: playerDoc.id,
        username: data.username,
        playerId: data.playerId,
        metric: metric,
        nameColour: data.nameColour || '§f',
        icon: data.icon || '',
        rankPrefix: data.rankPrefix || ''
      });
    }

    // Sort and filter out players with 0 for the selected color
    players.sort((a, b) => b.metric - a.metric);
    
    // Only show players with > 0 blocks when color is selected
    const filteredPlayers = selectedColor === 'all' 
      ? players 
      : players.filter(p => p.metric > 0);
    
    setPlayerLeaderboard(filteredPlayers);
  } catch (error) {
    console.error('Error loading player leaderboard:', error);
  }
};

  const loadColorLeaderboard = async () => {
    try {
      if (colorMetric === 'current') {
        const blocksSnapshot = await getDocs(collection(firestore, 'blocks'));
        const colorCounts = {};

        blocksSnapshot.forEach(doc => {
          const data = doc.data();
          const color = data.colour;
          
          if (color && color !== 'WHITE') {
            colorCounts[color] = (colorCounts[color] || 0) + 1;
          }
        });

        const colors = Object.entries(colorCounts).map(([color, count]) => ({
          color,
          count,
          hex: getColorHex(color)
        }));

        colors.sort((a, b) => b.count - a.count);
        setColorLeaderboard(colors);
      } else {
        const colorStatsDoc = await getDoc(doc(firestore, 'meta', 'colourStatsAllTime'));
        
        if (colorStatsDoc.exists()) {
          const data = colorStatsDoc.data();
          const colors = Object.entries(data)
            .filter(([color]) => color !== 'WHITE')
            .map(([color, count]) => ({
              color,
              count,
              hex: getColorHex(color)
            }));

          colors.sort((a, b) => b.count - a.count);
          setColorLeaderboard(colors);
        }
      }
    } catch (error) {
      console.error('Error loading color leaderboard:', error);
    }
  };

  const getColorHex = (colorName) => {
    const colorMap = {
      'WHITE': '#FFFFFF', 'BLACK': '#080A0F', 'BLUE': '#2D2F8F',
      'PINK': '#BE4B7A', 'BROWN': '#603A1E', 'CYAN': '#158690',
      'GREEN': '#495B24', 'GRAY': '#373A3E', 'LIGHT_BLUE': '#2AABCC',
      'LIGHT_GRAY': '#7D7D73', 'LIME': '#5EA818', 'MAGENTA': '#A8308C',
      'ORANGE': '#E06100', 'RED': '#8E2020', 'PURPLE': '#5F1F8F',
      'YELLOW': '#F0AF15'
    };
    return colorMap[colorName] || '#FFFFFF';
  };

  const convertMinecraftColor = (code) => {
    const colorMap = {
      '§0': '#000000', '§1': '#0000AA', '§2': '#00AA00', '§3': '#00AAAA',
      '§4': '#AA0000', '§5': '#AA00AA', '§6': '#FFAA00', '§7': '#AAAAAA',
      '§8': '#555555', '§9': '#5555FF', '§a': '#55FF55', '§b': '#55FFFF',
      '§c': '#FF5555', '§d': '#FF55FF', '§e': '#FFFF55', '§f': '#FFFFFF'
    };
    return colorMap[code] || '#FFFFFF';
  };

  const stripColorCodes = (text) => {
    if (!text) return '';
    return text.replace(/[§&$][0-9a-fk-or]/gi, '');
  };

  const handlePlayerClick = (playerId) => {
    navigate(`/player/${playerId}/profile`);
  };

  // Pagination logic
  const getCurrentPageData = () => {
    const data = activeTab === 'players' ? playerLeaderboard : colorLeaderboard;
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return data.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const data = activeTab === 'players' ? playerLeaderboard : colorLeaderboard;
    return Math.ceil(data.length / ITEMS_PER_PAGE);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="container leaderboards-page">
        <div className="loading">Loading leaderboards...</div>
      </div>
    );
  }

  const currentData = getCurrentPageData();
  const totalPages = getTotalPages();
  const startRank = (currentPage - 1) * ITEMS_PER_PAGE + 1;

  return (
    <div className="container leaderboards-page">
      <h1>Leaderboards</h1>

      <div className="leaderboard-tabs">
        <button
          className={activeTab === 'players' ? 'tab active' : 'tab'}
          onClick={() => { setActiveTab('players'); setCurrentPage(1); }}
        >
          Player Rankings
        </button>
        <button
          className={activeTab === 'colors' ? 'tab active' : 'tab'}
          onClick={() => { setActiveTab('colors'); setCurrentPage(1); }}
        >
          Colour Rankings
        </button>
      </div>

      {activeTab === 'players' && (
        <div className="leaderboard-section">
          <div className="leaderboard-controls">
  <div className="filter-group">
    <label>Rank by:</label>
    <select value={playerMetric} onChange={(e) => setPlayerMetric(e.target.value)}>
      <option value="total">Total Blocks</option>
      <option value="owned">Block Coverage</option>
    </select>
  </div>
  
  <div className="filter-group">
    <label>Color:</label>
    <select 
      value={selectedColor} 
      onChange={(e) => setSelectedColor(e.target.value)}
      className="color-filter-select"
    >
      <option value="all">All Colors</option>
      {ALL_COLORS.map(color => (
        <option key={color} value={color}>
          {color.replace('_', ' ')}
        </option>
      ))}
    </select>
  </div>
</div>

          <div className="leaderboard-table-container">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th className="rank-col">#</th>
                  <th className="player-col">Player</th>
<th className="stat-col">
  {playerMetric === 'total' ? 'Blocks Painted' : 'Blocks Owned'}
  {selectedColor !== 'all' && (
    <span className="color-filter-indicator">
      {' '}({selectedColor.replace('_', ' ')})
    </span>
  )}
</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((player, index) => {
                  const actualRank = startRank + index;
                  return (
                    <tr
                      key={player.uuid}
                      className="leaderboard-row"
                      onClick={() => handlePlayerClick(player.playerId)}
                    >
                      <td className="rank-col">
                        <span className={`rank-number ${actualRank <= 3 ? 'top-three' : ''}`}>
                          {actualRank}
                        </span>
                      </td>
                      <td className="player-col">
                        <div className="player-info">
                          <img
                            src={`https://visage.surgeplay.com/face/64/${player.uuid}`}
                            alt={player.username}
                            className="player-head"
                          />
                          <div className="player-name-container">
                            {/* {player.rankPrefix && (
                              <span className="rank-badge">
                                {stripColorCodes(player.rankPrefix)}
                              </span>
                            )} */}
                            {player.icon && (
                              <span
                                className="player-icon-lb"
                                style={{ color: convertMinecraftColor(player.icon.substring(0, 2)) }}
                              >
                                {stripColorCodes(player.icon)}
                              </span>
                            )}
                            <span
                              className="player-username"
                              style={{ color: convertMinecraftColor(player.nameColour) }}
                            >
                              {player.username}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="stat-col">
                        {player.metric.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="page-numbers">
                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                    return <span key={pageNum} className="page-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'colors' && (
        <div className="leaderboard-section">
          <div className="leaderboard-controls">
            <div className="filter-group">
              <label>Show:</label>
              <select value={colorMetric} onChange={(e) => setColorMetric(e.target.value)}>
                <option value="current">Current</option>
                <option value="ever">All Time</option>
              </select>
            </div>
          </div>

          <div className="leaderboard-table-container">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th className="rank-col">#</th>
                  <th className="color-col">Colour</th>
                  <th className="stat-col">
                    {colorMetric === 'current' ? 'Blocks on Map' : 'Total Placed'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((color, index) => {
                  const actualRank = startRank + index;
                  return (
                    <tr key={color.color} className="leaderboard-row color-row">
                      <td className="rank-col">
                        <span className={`rank-number ${actualRank <= 3 ? 'top-three' : ''}`}>
                          {actualRank}
                        </span>
                      </td>
                      <td className="color-col">
                        <div className="color-info">
                          <div
                            className="color-swatch"
                            style={{ backgroundColor: color.hex }}
                          />
                          <span className="color-name">{color.color}</span>
                        </div>
                      </td>
                      <td className="stat-col">
                        {color.count.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              <div className="page-numbers">
                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        className={`page-num ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                    return <span key={pageNum} className="page-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                className="page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaderboardsPage;